import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, Typography, Button, Alert, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Page, PageTitle, ActionBar, Sheet } from '../../components'
import useFetch from '../../hooks/useFetch'
import InlineLoader from '../../loading/InlineLoader'
import CycleHeader from './CycleHeader'
import LossList from './LossList'
import ExplainSheet from './ExplainSheet'
import FoundItSheet from './FoundItSheet'
import { priceLabel, storeList } from './reviewFormat'

function Section({ titleKey, explainKey, count, children }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <Grid container direction='column' sx={{ marginTop: '1.5rem' }}>
            <Button
                onClick={() => setOpen(value => !value)}
                endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
                <Typography variant='h6'>{t(titleKey)}{count != null && ` (${count})`}</Typography>
            </Button>
            <Collapse in={open} unmountOnExit>
                <Typography color='text.secondary' sx={{ marginBottom: '.5rem' }}>{t(explainKey)}</Typography>
                {children}
            </Collapse>
        </Grid>
    )
}

export default function CycleReview(props) {
    const { setAuth } = props
    const { t } = useTranslation()
    const { isPending, fetchResource, showAlert, alertMessage, setAlertMessage, setShowAlert } =
        useFetch({ setAuth, persistAlerts: true })

    const [report, setReport] = useState(null)
    const [repeatProductIds, setRepeatProductIds] = useState(new Set())
    const [explaining, setExplaining] = useState(null)
    const [finding, setFinding] = useState(null)
    const [showConfirmClose, setShowConfirmClose] = useState(false)
    const [showConfirmDiscard, setShowConfirmDiscard] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const [reloadToken, setReloadToken] = useState(0)
    // useFetch hands back a new fetchResource on every render, so it must never reach a
    // dependency array: the effect would re-run forever, hitting the API each pass.
    const latest = useRef({})
    latest.current = { fetchResource, setAlertMessage, setShowAlert, t }

    const reload = () => setReloadToken(token => token + 1)

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const { fetchResource: fetch, setAlertMessage: alert, setShowAlert: show, t: translate } = latest.current
            try {
                const response = await fetch({ resource: 'inventory-check/cycle-report', body: {}, loadingKey: 'report' })
                if (response?.error) throw new Error(response.error)
                if (cancelled) return
                const fresh = response?.report ?? null
                setReport(fresh)
                setRepeatProductIds(new Set((response?.priorExplanations ?? []).map(row => String(row.product_id))))
            } catch (error) {
                if (cancelled) return
                setReport(null)
                alert({ message: translate('inventoryCheck.review.loadError'), type: 'error' })
                show(true)
            } finally {
                if (!cancelled) setLoaded(true)
            }
        })()

        return () => { cancelled = true }
    }, [reloadToken])

    async function saveExplanation(reason) {
        const productId = explaining?.productId
        setExplaining(null)
        try {
            const response = await fetchResource({
                resource: 'inventory-check/explain',
                body: { cycleId: report.cycleId, productId, reason },
                loadingKey: 'explain',
            })
            if (response?.error) throw new Error(response.error)
            reload()
        } catch (error) {
            setAlertMessage({ message: t('inventoryCheck.review.explainError'), type: 'error' })
            setShowAlert(true)
        }
    }

    async function removeExplanation(productId) {
        try {
            const response = await fetchResource({
                resource: 'inventory-check/unexplain',
                body: { cycleId: report.cycleId, productId },
                loadingKey: 'explain',
            })
            if (response?.error) throw new Error(response.error)
            reload()
        } catch (error) {
            setAlertMessage({ message: t('inventoryCheck.review.explainError'), type: 'error' })
            setShowAlert(true)
        }
    }

    async function saveFound({ newark, longbranch }) {
        const row = finding
        setFinding(null)
        try {
            for (const [location, quantity] of [['newark', newark], ['longbranch', longbranch]]) {
                const response = await fetchResource({
                    resource: 'inventory-check/update-product-quantity',
                    body: { location, barcode: row.barcode, quantity, productType: null },
                    loadingKey: 'found',
                })
                if (response?.error) throw new Error(response.error)
            }
            setAlertMessage({ message: t('inventoryCheck.review.foundSaved'), type: 'success' })
            setShowAlert(true)
            reload()
        } catch (error) {
            setAlertMessage({ message: error.message || t('inventoryCheck.review.foundError'), type: 'error' })
            setShowAlert(true)
        }
    }

    async function handleClose() {
        setShowConfirmClose(false)
        try {
            const response = await fetchResource({
                resource: 'inventory-check/close-cycle',
                body: { cycleId: report.cycleId, archiveBarcodes: explainedRows.map(row => row.barcode) },
                loadingKey: 'close',
            })
            if (response?.error) throw new Error(response.error)

            const archived = (response?.report?.archived ?? []).length
            const spared = (response?.report?.skipped ?? []).length
            const message = spared ? t('inventoryCheck.review.closeSuccessCounts', { archived, spared })
                : archived ? t('inventoryCheck.review.closeSuccessArchived', { archived })
                : t('inventoryCheck.review.closeSuccess')

            setAlertMessage({ message, type: 'success' })
            setShowAlert(true)
            reload()
        } catch (error) {
            // The server's text is kept: on a mid-archive failure it is the only thing that says
            // products are already off the storefront and the cycle is still open.
            setAlertMessage({ message: error.message || t('inventoryCheck.review.closeError'), type: 'error' })
            setShowAlert(true)
        }
    }

    async function handleDiscard() {
        setShowConfirmDiscard(false)
        try {
            const response = await fetchResource({
                resource: 'inventory-check/discard-cycle',
                body: { cycleId: report.cycleId },
                loadingKey: 'close',
            })
            if (response?.error) throw new Error(response.error)
            setAlertMessage({ message: t('inventoryCheck.review.discardSuccess'), type: 'success' })
            setShowAlert(true)
            reload()
        } catch (error) {
            setAlertMessage({ message: error.message || t('inventoryCheck.review.discardError'), type: 'error' })
            setShowAlert(true)
        }
    }

    const needsReason = (report?.lost ?? []).filter(row => !row.explanation)
    const explainedRows = (report?.lost ?? []).filter(row => row.explanation)
    const bothFinished = Boolean(report?.bothStoresFinished)
    const canClose = bothFinished && needsReason.length === 0
    const waiting = storeList((report?.stores ?? []).filter(store => !store.finishedAt).map(store => store.location))

    const Review = () => (
        <Grid container direction='column' sx={{ width: '100%', maxWidth: '52rem' }}>
            <CycleHeader report={report} />

            <Grid container direction='column' sx={{ marginTop: '1.5rem' }}>
                <Typography variant='h6'>
                    {t('inventoryCheck.review.needsReasonHeading')} ({needsReason.length})
                </Typography>
                <Typography color='text.secondary'>{t('inventoryCheck.review.needsReasonExplain')}</Typography>
                {needsReason.length === 0
                    ? <Typography color='success.main' sx={{ marginTop: '.5rem' }}>
                        {t('inventoryCheck.review.allExplained')}
                    </Typography>
                    : <LossList
                        rows={needsReason}
                        onExplain={id => setExplaining((report.lost ?? []).find(row => row.productId === id))}
                        onFound={id => setFinding((report.lost ?? []).find(row => row.productId === id))}
                        repeatProductIds={repeatProductIds}
                    />}
            </Grid>

            {explainedRows.length > 0 && (
                <Grid container direction='column' sx={{ marginTop: '1.5rem' }}>
                    <Typography variant='h6'>
                        {t('inventoryCheck.review.explainedHeading')} ({explainedRows.length})
                    </Typography>
                    <Typography color='text.secondary'>{t('inventoryCheck.review.explainedExplain')}</Typography>
                    <Typography sx={{ marginTop: '.25rem' }}>
                        {t('inventoryCheck.review.lostValue', {
                            value: priceLabel(t, explainedRows.reduce((total, row) => total + (Number(row.price) || 0), 0)),
                        })}
                    </Typography>
                    <LossList
                        rows={explainedRows}
                        onUnexplain={removeExplanation}
                        repeatProductIds={repeatProductIds}
                    />
                </Grid>
            )}

            <Section
                titleKey='inventoryCheck.review.reconciledHeading'
                explainKey='inventoryCheck.review.reconciledExplain'
                count={(report.reconciled ?? []).length}
            >
                <LossList rows={report.reconciled ?? []} repeatProductIds={repeatProductIds} />
            </Section>

            <Section
                titleKey='inventoryCheck.review.correctionsHeading'
                explainKey='inventoryCheck.review.correctionsExplain'
                count={(report.corrections ?? []).length}
            >
                <LossList rows={report.corrections ?? []} repeatProductIds={repeatProductIds} />
            </Section>

            <Grid container direction='column' alignItems='center' sx={{ gap: '.5rem', marginTop: '2rem' }}>
                {!bothFinished && (
                    <Typography color='warning.main' textAlign='center'>
                        {t('inventoryCheck.review.closeDisabledBoth')}
                    </Typography>
                )}
                {bothFinished && needsReason.length > 0 && (
                    <Typography color='warning.main' textAlign='center'>
                        {t('inventoryCheck.review.closeBlockedUnexplained', { count: needsReason.length })}
                    </Typography>
                )}
                {!bothFinished && waiting && (
                    <Typography color='warning.main' textAlign='center'>
                        {t('inventoryCheck.review.waitingOn', { stores: waiting })}
                    </Typography>
                )}
                <Button
                    variant='contained'
                    color='error'
                    disabled={!canClose}
                    onClick={() => setShowConfirmClose(true)}
                >
                    {t('inventoryCheck.review.closeCount')}
                </Button>
                <Button color='inherit' onClick={() => setShowConfirmDiscard(true)}>
                    {t('inventoryCheck.review.discardCount')}
                </Button>
            </Grid>
        </Grid>
    )

    return (<>
        <ExplainSheet
            open={explaining != null}
            onClose={() => setExplaining(null)}
            onConfirm={saveExplanation}
        />

        <FoundItSheet
            open={finding != null}
            onClose={() => setFinding(null)}
            onConfirm={saveFound}
        />

        <Sheet
            open={showConfirmClose}
            onClose={() => setShowConfirmClose(false)}
            actions={
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: () => setShowConfirmClose(false) }}
                    confirm={{ label: t('common.confirm'), onClick: handleClose, tone: 'danger', disabled: isPending('close') }}
                />
            }
        >
            <Typography variant='h6' textAlign='center'>{t('inventoryCheck.review.closeHeading')}</Typography>
            <Typography textAlign='center' sx={{ marginTop: '.5rem' }}>
                {explainedRows.length
                    ? t('inventoryCheck.review.closeArchiveNote', { count: explainedRows.length })
                    : t('inventoryCheck.review.closeNoArchiveNote')}
            </Typography>
            <Typography textAlign='center' color='warning.main' sx={{ marginTop: '.5rem' }}>
                {t('inventoryCheck.review.closeIrreversible')}
            </Typography>
        </Sheet>

        <Sheet
            open={showConfirmDiscard}
            onClose={() => setShowConfirmDiscard(false)}
            actions={
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: () => setShowConfirmDiscard(false) }}
                    confirm={{ label: t('common.confirm'), onClick: handleDiscard, tone: 'danger', disabled: isPending('close') }}
                />
            }
        >
            <Typography variant='h6' textAlign='center'>{t('inventoryCheck.review.discardHeading')}</Typography>
            <Typography textAlign='center' sx={{ marginTop: '.5rem' }}>
                {t('inventoryCheck.review.discardNote')}
            </Typography>
        </Sheet>

        <Page alignItems='center'>
            {showAlert && (
                <Alert className='alert' severity={alertMessage.type} variant='filled' onClose={() => setShowAlert(false)}>
                    {alertMessage.message}
                </Alert>
            )}
            <PageTitle>{t('inventoryCheck.review.pageTitle')}</PageTitle>

            {!loaded
                ? <InlineLoader />
                : report ? Review() : <CycleHeader report={null} />}
            {loaded && isPending('report') && <InlineLoader />}
        </Page>
    </>)
}
